import React, { useState, useMemo } from 'react'
import { Form, Button, Card, Badge, Modal, Stack } from 'react-bootstrap'
import { useModal } from '../../../providers/ModalProvider/ModalProvider'
import { usePrintTickets } from '../../../providers/PrintTicketsProvider/PrintTicketsProvider'
import {
  formatNumber,
  getSinglarOrPluralWord,
} from '../../../utilities/helpers'
import { Spinner } from '../../LoadingContainer/Spinner'

export default function PrintVouchersForOrder({ order }) {
  const { closeModal } = useModal()
  const {
    handlePrintVouchersPDF,
    handlePrintVouchersThermal,
    preparingVouchersPDF,
    preparingVouchersThermal,
  } = usePrintTickets()

  const vouchers = useMemo(() => order?.vouchers || [], [order?.vouchers])
  const flexPack = order?.flex_pack
  const [selectedVouchers, setSelectedVouchers] = useState([])

  const getSelectedVoucherCount = () => selectedVouchers.length

  const clickPrintPDF = async () => {
    await handlePrintVouchersPDF(flexPack, selectedVouchers)
  }

  const clickPrintThermal = async () => {
    await handlePrintVouchersThermal(flexPack, selectedVouchers)
  }

  return (
    <div
      className="h-100 d-flex d-flex-column"
      style={{ backgroundColor: '#FFFFFF', overflow: 'hidden' }}
    >
      <Modal.Header closeButton className="border-0 pb-0">
        <div className=" d-flex flex-row gap-4 align-items-center justify-content-start">
          <Modal.Title className="h4 text-dark">Print vouchers</Modal.Title>
        </div>
      </Modal.Header>
      <div className=" mb-4 px-4 d-flex justify-content-between align-items-center">
        <p className="text-muted mb-0 small">
          List of vouchers associated to{' '}
          <span className="text-primary fw-medium">{order?.email}</span>
        </p>
        <span className="text-muted small">
          Vouchers total {formatNumber(vouchers.length)}
        </span>
      </div>

      <SelectVouchers
        flexPack={flexPack}
        vouchers={vouchers}
        selectedVouchers={selectedVouchers}
        setSelectedVouchers={setSelectedVouchers}
      />

      <Modal.Footer className="border-0 pt-0 px-4 d-flex flex-row justify-content-between align-items-center w-100">
        <div className="text-muted">
          {formatNumber(getSelectedVoucherCount())}{' '}
          {getSinglarOrPluralWord(
            getSelectedVoucherCount(),
            'Voucher'
          ).toLowerCase()}{' '}
          selected
        </div>
        <div className="d-flex gap-2">
          <Button
            variant="primary"
            disabled={getSelectedVoucherCount() === 0 || preparingVouchersPDF}
            onClick={clickPrintPDF}
            style={{ minWidth: '100px' }}
          >
            {preparingVouchersPDF ? (
              <Spinner variant="white" size="md" />
            ) : (
              'Print PDF'
            )}
          </Button>
          <Button
            variant="primary"
            disabled={
              getSelectedVoucherCount() === 0 || preparingVouchersThermal
            }
            onClick={clickPrintThermal}
          >
            {preparingVouchersThermal ? (
              <Spinner variant="white" size="md" />
            ) : (
              'Print Thermal'
            )}
          </Button>
        </div>
      </Modal.Footer>
    </div>
  )
}

export function SelectVouchers({
  flexPack,
  vouchers,
  selectedVouchers,
  setSelectedVouchers,
  hideSelectAll = false,
}) {
  const allVoucherIds = useMemo(
    () => vouchers.map((voucher) => voucher.id),
    [vouchers]
  )

  const globalCheckboxState = useMemo(() => {
    if (allVoucherIds.length === 0) return false
    if (selectedVouchers.length === allVoucherIds.length) return true
    if (selectedVouchers.length > 0) return 'indeterminate'
    return false
  }, [selectedVouchers, allVoucherIds])

  const handleVoucherSelect = (voucherToToggle) => {
    setSelectedVouchers((prevSelected) => {
      const isSelected = prevSelected.some(
        (voucher) => voucher.id === voucherToToggle.id
      )
      if (isSelected) {
        return prevSelected.filter(
          (voucher) => voucher.id !== voucherToToggle.id
        )
      }
      return [...prevSelected, voucherToToggle]
    })
  }

  const handleGlobalSelectAllChange = () => {
    if (selectedVouchers.length === vouchers.length) {
      setSelectedVouchers([])
    } else {
      setSelectedVouchers(vouchers)
    }
  }

  return (
    <Modal.Body className="py-3 px-4 d-flex flex-column">
      {!hideSelectAll && (
        <div className="d-flex align-items-center">
          <Form.Check
            type="checkbox"
            id="select-all-vouchers"
            checked={globalCheckboxState === true}
            ref={(input) => {
              if (input) {
                input.indeterminate = globalCheckboxState === 'indeterminate'
              }
            }}
            onChange={handleGlobalSelectAllChange}
            label="Select all"
            className="fw-semibold"
          />
        </div>
      )}

      <div className="flex-grow-1">
        <Card
          className="mb-3"
          style={{ outline: '1px solid #E6E8EC', borderRadius: '8px' }}
        >
          <Card.Header
            className="d-flex align-items-start"
            style={{
              backgroundColor: '#FCFCFD',
              padding: '16px',
              borderBottom: '1px solid #E6E8EC',
            }}
          >
            <div>
              <div
                className="mb-1"
                style={{
                  color: '#141416',
                  fontWeight: 500,
                  fontFamily: 'Poppins',
                  fontSize: '14px',
                  lineHeight: '24px',
                }}
              >
                {flexPack?.name || 'Flex Pack'}
              </div>
            </div>
          </Card.Header>
          <Card.Body
            className=" p-3 d-flex flex-column gap-3"
            style={{ backgroundColor: '#FFFFFF' }}
          >
            {vouchers.length === 0 ? (
              <div className="text-center text-muted py-3">
                No vouchers found
              </div>
            ) : (
              vouchers.map((voucher) => (
                <div
                  key={voucher.id}
                  className="d-flex justify-content-between align-items-center"
                >
                  <Form.Check
                    type="checkbox"
                    className="form-check-display m-0"
                    id={`voucher-${voucher.id}`}
                  >
                    <Form.Check.Input
                      type="checkbox"
                      checked={selectedVouchers.some(
                        (selected) => selected.id === voucher.id
                      )}
                      onChange={() => handleVoucherSelect(voucher)}
                    />
                    <Stack
                      direction="horizontal"
                      gap={3}
                      className="flex-grow-1"
                    >
                      <Form.Check.Label
                        id={`voucher-label-${voucher.id}`}
                        className="m-0 d-flex gap-3 w-100 h-100 align-items-center form-check-label-sm"
                      >
                        {voucher.code}
                      </Form.Check.Label>
                    </Stack>
                  </Form.Check>

                  <Stack direction="horizontal" gap={2}>
                    {voucher?.claimed && (
                      <Badge bg="secondary" className="small">
                        Redeemed
                      </Badge>
                    )}
                  </Stack>
                </div>
              ))
            )}
          </Card.Body>
        </Card>
      </div>
    </Modal.Body>
  )
}
